import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'

import AuthHeader from '../features/auth/AuthHeader'
import EyeIcon from '../components/EyeIcon'
import { useAuth } from '../features/auth/AuthContext'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // Signing in lands in the app, same as signup — not back on the marketing page.
  const redirectTo = location.state?.from ?? '/pipeline'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login({ email, password })
      navigate(redirectTo, { replace: true })
    } catch (err) {
      setError(err.message || 'Unable to sign in')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-pageFill flex flex-col">
      <AuthHeader />

      {/* M3.3: 40px from the navbar to the content, all breakpoints. */}
      <main className="flex-1 flex items-start justify-center px-6 pt-[40px]">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[416px] flex flex-col items-center gap-6 md:gap-8"
        >
          <h1 className="text-[28px] md:text-[40px] leading-[36px] md:leading-[48px] font-extrabold text-brand-dark text-center w-full">
            Welcome Back!
          </h1>

          <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col gap-2 items-end w-full">
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full h-[48px] bg-white border border-[#cbdcc3] rounded-full px-6 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none focus:border-brand-green"
              />

              <div className="relative w-full">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full h-[48px] bg-white border border-[#cbdcc3] rounded-full pl-6 pr-12 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none focus:border-brand-green"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>

              <Link
                to="/forgot-password"
                className="text-[14px] font-medium text-brand-green hover:text-brand-hover transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="text-[14px] text-red-600 font-medium text-center">
                {error}
              </p>
            )}

            {/* Set #4 Login 1: link sits 12px below the button on both breakpoints, so it
                is grouped with the button rather than spaced by the form's own gap. */}
            <div className="w-full flex flex-col items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                /* Figma `login` frame: 196×56 centred on desktop; full width on mobile. */
                className="w-full md:w-[196px] h-[56px] rounded-full bg-brand-primary hover:bg-brand-hover text-white text-[16px] font-semibold disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Signing in…' : 'Sign In'}
              </button>

              <p className="text-center text-[14px] text-brand-dark">
                New to REILM?{' '}
                <Link to="/signup" className="font-semibold text-brand-green hover:text-brand-hover transition-colors">Start Your Free Trial</Link>
              </p>
            </div>
          </div>
        </form>
      </main>
    </div>
  )
}

export default Login
